// Shared data constants for the Maintenance / Tasks module. Extracted from
// Maintenance.jsx (P3.c) so the page component and its sub-components
// (JobDetailModal, RoutineTab, pickers …) share one source of truth.

import { UNIFIED_SYSTEMS } from "../../../lib/systems";

// Task systems = the unified taxonomy (shared with suppliers + invoices).
export const SYSTEMS = UNIFIED_SYSTEMS;
export const STATUS = ["open", "waiting_approval", "approved", "waiting_prepay", "prepay_too_high", "prepay_done", "waiting_parts", "in_progress", "completed", "dispute", "done", "confirmed", "full_payment_confirmed", "cancelled"];
// Internal crew tasks (assignee_type === "crew") use this simple set —
// no approval / prepay / parts / vendor confirmation. "done" marks the work
// finished by the assignee; "confirmed" is the senior crew sign-off on a
// completed task (no invoice side-effects, unlike the vendor flow).
export const INTERNAL_STATUS = ["open", "in_progress", "done", "confirmed"];
// Vendor / external-supplier jobs keep the full lifecycle below. Flow:
// approved (auto on quote accept) → [waiting_prepay → prepay_done] (optional,
// owner issues prepayment) → waiting_parts / in_progress (supplier) →
// completed (supplier uploads invoice) → confirmed (owner signs off, invoice
// posts to Invoices) → full_payment_confirmed (supplier closes the job).
export const VENDOR_STATUS = ["open", "waiting_approval", "approved", "waiting_prepay", "prepay_too_high", "prepay_done", "waiting_parts", "in_progress", "completed", "dispute", "confirmed", "cancelled"];
// Point of no return: from "Prepay done" onward (money moved / work started)
// the job can no longer be cancelled. Mirrors backend _NO_CANCEL_STATUSES.
export const NO_CANCEL_STATUSES = new Set([
  "prepay_done", "waiting_parts", "in_progress", "completed", "dispute", "confirmed", "full_payment_confirmed",
]);
// Display order for the grouped-by-status view in the Tasks page. OPEN work
// surfaces first (fresh to-dos the crew should pick up), then the active /
// blocked workflow states, and finally the terminal states (completed, done,
// confirmed, cancelled) sink to the bottom.
export const STATUS_GROUP_ORDER = [
  "open", "in_progress", "waiting_parts", "waiting_prepay", "prepay_too_high", "prepay_done",
  "waiting_approval", "approved", "dispute", "completed", "done", "confirmed",
  "full_payment_confirmed", "cancelled",
];
// Statuses a Provider may never set themselves — mirrors backend
// _PROVIDER_FORBIDDEN_STATUSES so the dropdown stays consistent with
// what the API will actually accept. `approved` is auto-set on quote accept;
// `prepay_done` and `confirmed` are the owner's sign-offs. The supplier CAN
// set the final `full_payment_confirmed` to close the job.
export const PROVIDER_FORBIDDEN_STATUSES = new Set([
  "approved", "prepay_done", "confirmed", "dispute", "prepay_too_high",
]);
// Statuses hidden from the crew's (owner's) manual vendor dropdown:
// `approved` is set automatically when a quote is accepted, and
// `full_payment_confirmed` is the supplier's closing action. The owner KEEPS
// `prepay_done` (they confirm the prepayment) and `confirmed` (sign-off).
export const CREW_VENDOR_HIDDEN_STATUSES = new Set([
  "approved", "full_payment_confirmed",
]);
// Fully ONLINE managed jobs (accepted marketplace quote — user rules 2026-06):
// once approved, the yacht side only gets these per-stage actions; the
// supplier drives every other forward step. From "waiting_parts" on, the
// owner cancels via an approval request instead (mirrors backend
// _ONLINE_OWNER_ALLOWED / _online_rules_gate).
export const ONLINE_OWNER_ALLOWED = {
  approved: ["cancelled"],
  waiting_prepay: ["prepay_done", "prepay_too_high", "cancelled"],
  prepay_too_high: ["cancelled"],
  prepay_done: ["cancelled"],
  waiting_parts: [],
  in_progress: [],
  completed: ["confirmed", "dispute"],
  dispute: [],
};
export const PRIORITY = ["low", "medium", "high", "critical"];

// "provider" is the internal role name for online suppliers — always show
// "Supplier" in the UI. Shared by the Tasks page and JobDetailModal.
export const displayRole = (r) => (r === "provider" ? "Supplier" : r);

// Engine-like systems — these expose a per-unit sub-menu in the periodic
// task editor so the user can pin the routine to a SPECIFIC engine /
// generator / tender engine / stabilizer on this yacht. Independent of
// the cycle basis (days vs engine hours) — picking "Engines" always
// shows the engine list, picking "Generators" always shows generators,
// regardless of how the cycle counts.
export const ENGINE_LIKE_SYSTEMS = new Set(["Engines & Propulsion", "Generators", "Tenders & Toys", "Stabilizers"]);

export const DEFAULT = {
  title: "", system_category: "", system_categories: [], priority: "medium", status: "open",
  due_date: "", description: "", cost: "", notes: "", assigned_to: "",
  parts: [],            // [{item_id, item_name, qty}]
  before_photos: [],    // [storage_path]
  // Offline / external supplier mode — picked from the toggle inside the
  // editor. When `assignee_type === "external"` the crew dropdown is hidden
  // and the supplier picker takes over. `external_supplier` ends up shaped
  // {supplier_id, supplier_name} on POST, or null when the toggle is "crew".
  assignee_type: "crew",      // "crew" | "external"
  external_supplier_id: "",
  external_supplier_name: "",
  // "Find supplier" mode — multi-select suppliers to request quotes from on
  // save. Each entry: {kind: "marketplace"|"private", id, name}.
  _quote_suppliers: [],
  // Inline "add new supplier" flow for offline vendors — mirrors the
  // Invoices.jsx picker so the UX is consistent. When `_new_supplier`
  // is true, the picker showed "+ Add new supplier" and the user gets
  // a name + Yes/No save-to-network choice.
  _new_supplier: false,
  _new_supplier_save: null,    // null = no choice yet, true/false after click
  _new_supplier_type: "company",
  _new_supplier_category: "other",
  _new_supplier_contact_person: "",
  _new_supplier_email: "",
  _new_supplier_phone: "",
};

export const ROUTINE_DEFAULT = {
  // Empty by default — the user MUST pick a System before saving (the
  // editor disables Save until system_category is set).
  title: "", system_category: "", system_categories: [], priority: "medium",
  description: "", assigned_to: "",
  recurrence_kind: "days", recurrence_value: 30,
  // How many days after a job is generated it's due. Blank → server uses the
  // cycle interval (days mode) or a 14-day default (engine-hours mode).
  due_days: "",
  // "Whichever comes first" secondary trigger (UI: has_secondary). When on,
  // secondary_value is the OPPOSITE-unit interval (hours if primary is days,
  // days if primary is hours). Maps to the backend `secondary_value` field.
  has_secondary: false,
  secondary_value: "",
  // First-due override for days-mode. When blank, the server falls back
  // to "now + recurrence_value days" — same as before. When set, the
  // user is pinning the very first scheduled service to a specific
  // calendar date (e.g. annual safety check anchored to a survey date).
  // ISO date string `YYYY-MM-DD` straight from <input type="date">.
  first_due_date: "",
  // Engine-hours mode only:
  next_due_hours: "",
  engine_target_kind: "engine",
  engine_target_id: "",
  engine_group: "",
  parts: [], enabled: true,
  quote_suppliers: [],
  // Offline / external supplier — same toggle pattern as the one-off
  // editor. When `assignee_type === "external"` every spawned job inherits
  // the external_supplier field on the routine template.
  assignee_type: "crew",       // "crew" | "external"
  external_supplier_id: "",
  external_supplier_name: "",
  _new_supplier: false,
  _new_supplier_save: null,
  _new_supplier_type: "company",
  _new_supplier_category: "other",
  _new_supplier_contact_person: "",
  _new_supplier_email: "",
  _new_supplier_phone: "",
};
